import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  bool,
  func,
  number,
  oneOf,
  string
} from 'prop-types';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { Row } from '@thd-olt-component-react/grid';
import { Overlay } from '@thd-olt-component-react/overlay';
import { useMultipleSamples } from '../../hooks/useMultipleSamples';
import { Sample } from '../Sample/Sample';
import { TraySample } from './components/TraySample';
import { TrayHeader } from './components/TrayHeader';
import { getCents, getDollars } from '../../helpers';
import { publish } from '../../analytics';
import './sample-overlay.scss';

export const SampleOverlay = ({
  channel,
  dimensionText,
  open,
  onClose,
  parentId,
  samplesPerRow,
  storeId,
  zipCode
}) => {
  const { data, loading } = useMultipleSamples({ parentId, storeId, zipCode });

  const { orderSamples } = data;

  const [traySamples, setTraySamples] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const touchDevice = useRef();

  useEffect(() => {
    if (document && document.documentElement) {
      touchDevice.current = 'ontouchstart' in document.documentElement;
    }

    // publish order samples click for analytics
    publish('order-sample.click');

    // remove selected samples on unmount
    return () => {
      setTraySamples([]);
    };
  }, []);

  if (!orderSamples?.length || loading || !data) {
    return null;
  }

  const handleAddToCartClick = () => {
    // close overlay
    onClose();

    const skus = traySamples.map((sample) => sample.itemId);

    // for analytics
    const payload = {
      skus
    };

    publish('order-sample.atc', payload);
  };

  const isMobile = channel === 'mobile';
  const shouldExpandDiv = traySamples.length > 0 && isMobile;
  const shouldExpandDivMore = traySamples.length > samplesPerRow && isMobile;

  const headerTextClasses = classNames('sample-overlay__header__text', {
    'sample-overlay__header__text--small-font': isMobile
  });
  const contentClasses = classNames('sample-overlay__content', {
    'sample-overlay__content--expanded-one': shouldExpandDiv,
    'sample-overlay__content--expanded-two': shouldExpandDivMore
  });

  const deliveryClass = classNames({
    'sample-overlay__footer__delivery-absolute': channel === 'desktop',
    'sample-overlay__footer__delivery': channel === 'mobile'
  });

  const computeSubtotal = (amount) => {
    const calculatedSubtotal = amount + subtotal;
    setSubtotal(Math.abs(calculatedSubtotal));
  };

  const addSampleToTray = (sample, amount) => {
    setTraySamples([...traySamples, sample]);
    computeSubtotal(amount);
  };

  const removeSampleFromTray = (sample, amount) => {
    const { itemId } = sample;
    const updatedTray = traySamples.filter((samp) => samp.itemId !== itemId);
    setTraySamples(updatedTray);
    computeSubtotal(-amount);
  };

  const handleSampleClick = (sample) => {
    const { itemId, price = null } = sample;
    const sampleInTray = traySamples.find((samp) => samp.itemId === itemId);
    if (!sampleInTray) {
      addSampleToTray(sample, price);
    } else {
      removeSampleFromTray(sample, price);
    }
  };

  const getCartReqParams = (samples) => {
    return samples.map((sample) => {
      return {
        fulfillmentMethod: 'ShipToHome',
        fulfillmentLocation: zipCode,
        itemId: sample?.itemId,
        quantity: 1
      };
    });
  };

  const renderTraySamples = (samples) => {
    return samples.map((sample, sampleIndex) => {

      const { imgUrl, price = null } = sample;

      return (
        <div key={`tray-sample-${sampleIndex}`}>
          <TraySample
            channel={channel}
            onClick={() => removeSampleFromTray(sample, price)}
            imageUrl={imgUrl}
          />
        </div>
      );
    });
  };

  const expandedDivClasses = classNames('sample-overlay__expanded-div', {
    'sample-overlay__expanded-div--open': traySamples.length > 0 && isMobile
  });

  const expandDiv = (samples) => {
    return (
      <div className={expandedDivClasses}>
        <div className="sample-overlay__tray-samples-wrapper">
          <TrayHeader channel={channel} dimensions={dimensionText} subtotal={subtotal} />
          {renderTraySamples(samples)}
        </div>
      </div>
    );
  };

  const renderInStockScenario = (amount) => {
    return (
      <span className="sample-overlay__sample-content__price">
        {amount < 1
          ? <span>{getCents(amount)}&cent;</span>
          : <span>${getDollars(amount)}</span>}
      </span>
    );
  };

  const outOfStockMessage = (
    <span className="sample-overlay__sample-content__descriptor">
      <span className="sample-overlay__close-x-black" />
      Out of stock
    </span>
  );

  const discontinuedMessage = (
    <span className="sample-overlay__sample-content__descriptor">
      <span className="alert-inline alert-inline--danger">
        Discontinued
      </span>
    </span>
  );

  const renderSamples = (samples) => {
    let scenario;
    return samples.map((sample, sampleIndex) => {

      const {
        color = '',
        imgUrl = '',
        discontinued = false,
        inStockStatus = false,
        itemId = '',
        price = null,
      } = sample;

      const imageElements = imgUrl.split('/');
      if (imageElements.find((element) => element.includes('No_Image'))) {
        return null;
      }

      if (inStockStatus) {
        scenario = renderInStockScenario(price);
      } else if (discontinued) {
        scenario = discontinuedMessage;
      } else {
        scenario = outOfStockMessage;
      }

      const selected = traySamples.find((samp) => samp.itemId === itemId);

      return (
        <div className="sample-overlay__col-custom-width" key={`sample-${sampleIndex}`}>
          <Sample
            channel={channel}
            selectable={!!inStockStatus}
            onClick={inStockStatus ? () => handleSampleClick(sample) : null}
            imageUrl={imgUrl}
            selected={!!selected}
            selectButton
            touchDevice={touchDevice?.current}
          >
            <div className="sample-overlay__sample-content">
              <span className="sample-overlay__sample-content__descriptor">{color}</span>
              {scenario}
            </div>
          </Sample>
        </div>
      );
    });
  };

  return (
    <Overlay
      closeButton
      open={open}
      onClose={onClose}
    >
      <Row className="sample-overlay__header">
        <span className={headerTextClasses}>
          Choose Samples
        </span>
      </Row>
      <div className={contentClasses}>
        {renderSamples(orderSamples)}
      </div>
      <Row className="sample-overlay__footer">
        <div className="sample-overlay__tray-header-wrapper">
          <TrayHeader channel={channel} dimensions={dimensionText} subtotal={subtotal} />
        </div>
        <div className="sample-overlay__footer__purchase-wrapper">
          <div className="sample-overlay__footer__samples">
            {!traySamples.length && !isMobile
              ? (
                <span className="sample-overlay__footer__nosamples-text">
                  You currently have no samples selected.
                </span>
              )
              : (
                <div className="sample-overlay__samples-overflow-wrapper">
                  <div className="sample-overlay__footer__tray-samples">
                    {renderTraySamples(traySamples)}
                  </div>
                </div>
              )}
          </div>
          {expandDiv(traySamples)}
          <div className="sample-overlay__footer__atc">
            <AddToCart
              cartOptions={{
                channel,
                directCheckout: true,
                host: window.location.origin
              }}
              cartReqParams={getCartReqParams(traySamples)}
              disabled={!traySamples.length}
              onClick={() => handleAddToCartClick(traySamples)}
            >
              Add to Cart
            </AddToCart>
          </div>
        </div>
        <div className={deliveryClass}>
          <span>
            Fast and Free Delivery
          </span>
        </div>
      </Row>
    </Overlay>
  );
};

SampleOverlay.displayName = 'SampleOverlay';

SampleOverlay.propTypes = {
  channel: oneOf(['desktop', 'mobile', 'tablet']),
  dimensionText: string,
  samplesPerRow: number,
  open: bool,
  onClose: func.isRequired,
  parentId: string.isRequired,
  storeId: string.isRequired,
  zipCode: string.isRequired
};

SampleOverlay.defaultProps = {
  channel: 'desktop',
  dimensionText: null,
  open: false,
  samplesPerRow: 6
};
